import { useTranslation } from 'react-i18next'
import { object } from 'yup'
import { ConfirmRadioOptions, ConfirmRadioValues } from 'constants/Forms'
import { useVerticalContext } from 'hooks/context/useVerticalContext'
import { useFormFields } from 'hooks/forms/useFormFields'
import { useFormSetup } from 'hooks/forms/useFormSetup'
import { useYupFields } from 'hooks/forms/useYupFields'
import { useRegion } from 'hooks/useRegion'
import { FormFieldsType, FormSchemaType } from 'types/formField'
import { BusinessDetailsPayload } from 'types/verification'
import { getIndustryName } from 'utils/utils'
import {
  BusinessDetailsFormKeyList,
  BusinessDetailsFormKeys,
  IncomeTaxNumberRegions,
  RegionFallbackValue,
} from './constants'
import { BusinessDetailsFormData, BusinessDetailsFormKey } from './types'

export type BusinessDetailsSchema = FormSchemaType<BusinessDetailsFormKey>
export type BusinessDetailsFields = FormFieldsType<BusinessDetailsFormKey>

export const useBusinessDetailsForm = () => {
  const { t } = useTranslation()
  const { businessDetails: savedData, industryOptions } = useVerticalContext()
  const { businessCountryCode } = useRegion()
  const { getYupField, getRequiredYupField, getRequiredRadioYupField, getConditionalYupField } =
    useYupFields()

  const isIncomeTaxNumberRequired = IncomeTaxNumberRegions?.includes(businessCountryCode)

  const getDefaultValue = (key: BusinessDetailsFormKey) => {
    if (key === BusinessDetailsFormKeys.IS_OPERATION_LICENSE_REQUIRED) {
      const savedValue = savedData?.[key]
      if (typeof savedValue !== 'boolean') return null
      return savedValue ? ConfirmRadioValues.YES : ConfirmRadioValues.NO
    }
    const defaultValue = savedData?.[key] ?? ''
    switch (key) {
      case BusinessDetailsFormKeys.INDUSTRY_REFERENCE:
        const industryName = getIndustryName(defaultValue, industryOptions)
        return !!industryName ? defaultValue : ''
      default:
        return defaultValue
    }
  }

  const defaultValues = BusinessDetailsFormKeyList?.reduce((defaultValuesObj, key) => {
    defaultValuesObj[key] = getDefaultValue(key)
    return defaultValuesObj
  }, {} as BusinessDetailsFormData)

  const getFieldSchema = (key: BusinessDetailsFormKey) => {
    const label = t(`businessDetails.labels.${key}`)
    const requiredErrorMessages: Partial<Record<BusinessDetailsFormKey, string>> = {
      isOperationLicenseRequired: t('emptySelect'),
      operatingLicenseType: t('operatingLicenseError.type'),
      operatingLicenseIssuer: t('operatingLicenseError.issuer'),
    }
    const requiredErrorMessage = requiredErrorMessages?.[key]
    switch (key) {
      case BusinessDetailsFormKeys.INCOME_TAX_NUMBER:
        const regionTranslationKey = isIncomeTaxNumberRequired
          ? businessCountryCode
          : RegionFallbackValue
        const regionLabel = t(`businessDetails.labels.${key}.${regionTranslationKey}`)
        return isIncomeTaxNumberRequired
          ? getRequiredYupField({ label: regionLabel })
          : getYupField({ label: regionLabel })
      case BusinessDetailsFormKeys.IS_OPERATION_LICENSE_REQUIRED:
        return getRequiredRadioYupField(label, requiredErrorMessage)
      case BusinessDetailsFormKeys.OPERATING_LICENSE_TYPE:
      case BusinessDetailsFormKeys.OPERATING_LICENSE_ISSUER:
        return getConditionalYupField({
          fieldKey: BusinessDetailsFormKeys.IS_OPERATION_LICENSE_REQUIRED,
          fieldValue: ConfirmRadioValues.YES,
          label,
          requiredErrorMessage,
        })
      default:
        return getRequiredYupField({ label, requiredErrorMessage })
    }
  }

  const schemaObj = BusinessDetailsFormKeyList?.reduce((schemaObj, key) => {
    schemaObj[key] = getFieldSchema(key)
    return schemaObj
  }, {} as BusinessDetailsSchema)

  const formSchema = object<BusinessDetailsSchema>(schemaObj)

  const { form, formValues } = useFormSetup<BusinessDetailsFormData>(formSchema, defaultValues)
  const formErrors = form?.formState?.errors || {}
  const isFormDirty = form?.formState?.isDirty

  const { setInputField, setSelectField, setRadioField } = useFormFields<BusinessDetailsFormKey>(
    formSchema,
    form,
    formValues
  )

  const getFieldProps = (key: BusinessDetailsFormKey) => {
    const placeholders: Partial<Record<BusinessDetailsFormKey, string>> = {
      incomeTaxNumber: t('formFields.enterNumber'),
      industryReference: t('verification.chooseIndustry'),
    }
    switch (key) {
      case BusinessDetailsFormKeys.INDUSTRY_REFERENCE:
        return setSelectField({
          key,
          placeholder: placeholders?.[key],
          options: industryOptions,
        })
      case BusinessDetailsFormKeys.IS_OPERATION_LICENSE_REQUIRED:
        return setRadioField({
          key,
          options: ConfirmRadioOptions?.map(({ label, value }) => ({ label: t(label), value })),
          onChangeAdditional: value => {
            if (value !== ConfirmRadioValues.YES) {
              form?.clearErrors([
                BusinessDetailsFormKeys.OPERATING_LICENSE_TYPE,
                BusinessDetailsFormKeys.OPERATING_LICENSE_ISSUER,
              ])
            }
          },
        })
      default:
        return setInputField({ key, placeholder: placeholders?.[key] })
    }
  }

  const formFields = BusinessDetailsFormKeyList?.reduce((fieldsObj, key) => {
    fieldsObj[key] = getFieldProps(key)
    return fieldsObj
  }, {} as BusinessDetailsFields)

  const getPayloadValue = (key: BusinessDetailsFormKey) => {
    const formValue = formValues?.[key]
    const isOperationLicenseRequired =
      formValues?.isOperationLicenseRequired === ConfirmRadioValues.YES
    switch (key) {
      case BusinessDetailsFormKeys.IS_OPERATION_LICENSE_REQUIRED:
        return isOperationLicenseRequired
      case BusinessDetailsFormKeys.OPERATING_LICENSE_TYPE:
      case BusinessDetailsFormKeys.OPERATING_LICENSE_ISSUER:
        return isOperationLicenseRequired ? formValue : null
      default:
        return formValue
    }
  }

  const getPayload = () => {
    return BusinessDetailsFormKeyList?.reduce((payloadObj, key) => {
      payloadObj[key] = getPayloadValue(key)
      return payloadObj
    }, {}) as BusinessDetailsPayload
  }

  return {
    form,
    formValues,
    formFields,
    formErrors,
    isFormDirty,
    isIncomeTaxNumberRequired,
    getPayload,
  }
}
